import React, { useCallback, useEffect, useRef, useState } from 'react';
import { KpPanel } from '../ui/panel';
import { useToast } from '../ui/ToastProvider';
import ForslagFormular from './ForslagFormular';
import { skapaForslag, laddaUppFil } from './forslagApi';
import { OPPNA_FORSLAG, OPPNA_FORSLAG_GAMMAL } from './oppnaForslag';
import { byggAnrop, sorteraFiler, typFor, TYPER, KALLOR } from './forslagRegler';
import { useIssueContext } from '../../modules/Feedback/useIssueContext';
import './Forslag.css';

/**
 * ForslagPanel – den enda vägen in för buggar, förbättringar, nya funktioner och frågor (BL-169).
 * Monteras en gång i Layout för alla inloggade. Öppnas med oppnaForslag({ kalla, typ, modul, rubrik })
 * från sidfoten, hörnknappen och På gång. Högerpanel KpPanel md i formulärläge (DS Del Y, HP-43).
 * Allt sparas i feedback_items via POST /api/v1/feedback, med ingången i context.kalla.
 */
const TOMT = { typKey: 'bug', rubrik: '', beskrivning: '', modul: '', allvarlighet: '', paverkan: '' };

let versionCache = null;
function hamtaAppVersion() {
  const env = typeof process !== 'undefined' && process.env && process.env.REACT_APP_VERSION;
  if (env) return Promise.resolve(env);
  if (versionCache) return versionCache;
  versionCache = fetch('/version.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => (d && d.app && d.app.version) || null)
    .catch(() => null);
  return versionCache;
}

function forvalTillVarden(f) {
  const typ = TYPER.some((t) => t.key === f.typ) ? f.typ : (f.kalla === 'pa_gang' ? 'improvement' : 'bug');
  return { ...TOMT, typKey: typ, modul: f.modul || '', rubrik: (f.rubrik || '').slice(0, 90) };
}

export default function ForslagPanel() {
  const [open, setOpen] = useState(false);
  const [kalla, setKalla] = useState('fab');
  const [varden, setVarden] = useState(TOMT);
  const [filer, setFiler] = useState([]);
  const [filfel, setFilfel] = useState([]);
  const [fel, setFel] = useState({});
  const [skickar, setSkickar] = useState(false);
  const [skickfel, setSkickfel] = useState('');
  const [klar, setKlar] = useState(null);
  const getContext = useIssueContext();
  const { push } = useToast();
  const autoModulRef = useRef('');
  const skickarRef = useRef(false);

  const oppna = useCallback((forval) => {
    if (skickarRef.current) return;
    const f = forval || {};
    setKalla(KALLOR.includes(f.kalla) ? f.kalla : 'fab');
    setVarden(forvalTillVarden(f));
    setFiler([]); setFilfel([]); setFel({}); setSkickfel(''); setKlar(null);
    const ctx = getContext();
    autoModulRef.current = (ctx && ctx.module && ctx.module !== 'Övrigt') ? ctx.module : '';
    setOpen(true);
  }, [getContext]);

  useEffect(() => {
    const nya = (e) => oppna(e.detail);
    const gamla = () => oppna({ kalla: 'fab' });
    window.addEventListener(OPPNA_FORSLAG, nya);
    window.addEventListener(OPPNA_FORSLAG_GAMMAL, gamla);
    return () => {
      window.removeEventListener(OPPNA_FORSLAG, nya);
      window.removeEventListener(OPPNA_FORSLAG_GAMMAL, gamla);
    };
  }, [oppna]);

  // Hörnknappen och KanAI-knappen ligger nere till höger och döljs medan panelen är öppen.
  useEffect(() => {
    document.body.classList.toggle('kp-forslag-open', open);
    document.body.classList.toggle('fb-panel-open', open);
    return () => {
      document.body.classList.remove('kp-forslag-open');
      document.body.classList.remove('fb-panel-open');
    };
  }, [open]);

  const satt = useCallback((falt, v) => {
    setVarden((prev) => ({ ...prev, [falt]: v }));
    if (falt === 'rubrik') setFel((prev) => ({ ...prev, rubrik: undefined }));
  }, []);

  const laggTill = useCallback((nya) => {
    setFiler((prev) => {
      const { ok, fel: f } = sorteraFiler(nya, prev.length);
      setFilfel(f);
      return [...prev, ...ok];
    });
  }, []);

  const taBort = useCallback((i) => { setFiler((prev) => prev.filter((_, j) => j !== i)); setFilfel([]); }, []);

  // Klistra in en skärmbild eller fil var som helst i panelen.
  const onPaste = (e) => {
    const items = Array.from((e.clipboardData && e.clipboardData.files) || []);
    if (!items.length) return;
    e.preventDefault();
    laggTill(items.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `inklistrad-${Date.now()}-${i + 1}.${(f.type.split('/')[1] || 'png')}`, { type: f.type }))));
  };

  // Under skick stängs panelen inte (Esc, X, gråtoningen), annars försvinner referensen.
  const stang = () => { if (!skickarRef.current) setOpen(false); };

  const skicka = async () => {
    if (skickar) return;
    if (varden.rubrik.trim().length < 3) { setFel({ rubrik: 'Skriv en rubrik på minst 3 tecken.' }); return; }
    skickarRef.current = true;
    setSkickar(true);
    setSkickfel('');
    try {
      const ctx = getContext();
      const anrop = byggAnrop({ ...varden, modul: varden.modul || (kalla === 'pa_gang' ? '' : autoModulRef.current) }, ctx, kalla, await hamtaAppVersion());
      const res = await skapaForslag(anrop);
      const misslyckade = [];
      for (const fil of filer) {
        try { await laddaUppFil(res.id, fil); } catch (err) { misslyckade.push(err.message || fil.name); }
      }
      setKlar({ id: res.id, typKey: varden.typKey, misslyckade });
      push({ type: 'success', message: `Tack. Ditt ärende har referens ${res.id}.` });
      setFiler([]);
    } catch (err) {
      setSkickfel(err.message || 'Förslaget kunde inte skickas. Försök igen.');
    } finally {
      skickarRef.current = false;
      setSkickar(false);
    }
  };

  const t = typFor(varden.typKey);
  const dirty = !klar && !skickar && (varden.rubrik.trim() !== '' || varden.beskrivning.trim() !== '' || filer.length > 0);

  const footer = klar ? (
    <button type="button" className="forslag-btn forslag-btn--primar" onClick={stang}>Stäng</button>
  ) : ({ requestClose }) => (
    <>
      {skickfel && <span className="forslag-skickfel" role="alert">{skickfel}</span>}
      <button type="button" className="forslag-btn forslag-btn--sek" onClick={requestClose} disabled={skickar}>Avbryt</button>
      <button type="button" className="forslag-btn forslag-btn--primar" onClick={skicka} disabled={skickar}>
        {skickar ? 'Skickar...' : t.knapp}
      </button>
    </>
  );

  return (
    <KpPanel
      open={open}
      onClose={stang}
      size="md"
      mode="form"
      dirty={dirty}
      className="forslag-panel"
      context="Support och förslag"
      title={klar ? 'Tack, det är mottaget' : t.titel}
      footer={footer}
    >
      <div onPaste={klar ? undefined : onPaste}>
        {klar ? (
          <div className="forslag-klar" role="status">
            <div className="forslag-klar__ikon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <p className="forslag-klar__text">Tack. Ditt ärende har referens</p>
            <p className="forslag-klar__ref">{klar.id}</p>
            <p className="forslag-klar__sub">Ange referensen om du vill följa upp. Systemansvarig går igenom alla inskick och lägger in dem i planeringen.</p>
            {klar.misslyckade.length > 0 && (
              <ul className="forslag-filer__fel" role="alert">
                {klar.misslyckade.map((m) => <li key={m}>{m}</li>)}
              </ul>
            )}
          </div>
        ) : (
          <ForslagFormular
            varden={varden}
            satt={satt}
            filer={filer}
            filfel={filfel}
            onLaggTillFiler={laggTill}
            onTaBortFil={taBort}
            fel={fel}
            autoModul={kalla === 'pa_gang' ? '' : autoModulRef.current}
          />
        )}
      </div>
    </KpPanel>
  );
}
